import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useWhatsNew } from '@/components/whats-new/whats-new-provider';
import { useBillingStatus } from '@/hooks/use-billing-status';
import { useOpenModalsCount } from '@/hooks/use-open-modals-count';
import { useTenant } from '@/hooks/use-tenant';
import { type AutoOpenInput, shouldAutoOpen } from '@/lib/whats-new/auto-open';

/** NEWS-15: the edition opens this long after the Início mounts. */
export const AUTO_OPEN_DELAY_MS = 900;

/**
 * Opens the newest unseen edition on its own (NEWS-15…21), for the Início:
 * 900 ms after the page mounts, judged by `shouldAutoOpen` at that moment
 * (a catalog still loading then doesn't open it in this visit).
 */
export function useWhatsNewAutoOpen(): void {
  const { latest, status, seenInSession, open } = useWhatsNew();
  const tenant = useTenant();
  const pathname = useLocation().pathname;
  const { data: billingStatus } = useBillingStatus();
  const { openModalsCount } = useOpenModalsCount();

  // SPEC_DEVIATION: NEWS-16's "402 billing_locked" is read from the billing
  // status (`blocked_dashboard`/`blocked_apps`, the PastDueLockModal rule).
  // Reason: the 402 guard (AD-043) is not on `dev` yet; the status is what
  // the admin knows about a locked store today.
  const billingLocked =
    billingStatus?.state === 'blocked_dashboard' ||
    billingStatus?.state === 'blocked_apps';

  const input: Omit<AutoOpenInput, 'root'> = {
    pathname,
    tenantSlug: tenant.slug,
    isAppUnderConstruction: tenant.isAppUnderConstruction,
    billingLocked,
    openModalsCount,
    status,
    latest,
    seenInSession,
  };
  const inputRef = useRef(input);
  inputRef.current = input;
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const current = inputRef.current;
      if (current.latest && shouldAutoOpen({ ...current, root: document })) {
        openRef.current(current.latest.id);
      }
    }, AUTO_OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [tenant.slug]);
}
